import React from 'react';

/*
    Interval Timer Statuses:
    STOPPED: Starting State, Timer not in use
    STARTING: Timer has been started, but timer is still being initialized. Will soon transition to RUNNING.
    RUNNING: Timer is active and counting down.
    STOPPING: Timer has been stopped (via stopTimer) but timer is still cleaning up.
    (Once this state is reached, the callback should NOT be triggered if expired.)
    TRIGGERED: The timer has expired and the callback has been invoked.
    (NOTE: Because React buffers state changes, the state of the timer may still show as "RUNNING" if inspected prior to the
    callback being completed. TODO: Make invoking the function asyncronous outside of the useEffect so this doesn't happen.)
    If loop, the state will return to RUNNING upon conclusion of the callback, otherwise it will be STOPPED.
    TRIGGERED_HALT: A Combination of TRIGGERED and STOPPING. While TRIGGERED, the timer was requested to stop.
    Upon completion of the callback, the timer will be cleaned up and move to STOPPED, even if looping.
*/
export enum IntervalTimerStatus 
{ 
    STOPPED = 1, 
    STARTING = 2, 
    RUNNING = 3, 
    STOPPING = 4,   
    TRIGGERED = 5, 
    TRIGGERED_HALT = 6 
}

const useIntervalTimer = (loop: boolean, onTimerExpiration: () => void) => { 


    const [timeRemaining, setTimeRemaining] = React.useState(-1); 
    const [timerStatus, setTimerStatus] = React.useState(IntervalTimerStatus.STOPPED); 
    const [timeInterval, setTimeInterval] = React.useState(0); 

    /* 
    When react re-renders most functions, it generates a new memory refrence which forces a full re-render of a hook.
    This will update the function 'behind the scenes' without forcing a full rerender.
    */
    const onTimerExpirationRef = React.useRef(onTimerExpiration);
    onTimerExpirationRef.current = onTimerExpiration;


    const startTimer = (newTimeInterval?: number) => 
    { 
        if (timerStatus !== IntervalTimerStatus.STOPPED) 
        { 
            return false; 
        } 
        else 
        { 
            if (newTimeInterval) setTimeInterval(newTimeInterval); 
            setTimerStatus(IntervalTimerStatus.STARTING); 
            return true;
        } 
    }; 


  const stopTimer = () => 
    { 
        switch (timerStatus) 
        { 
            case IntervalTimerStatus.RUNNING: 
                setTimerStatus(IntervalTimerStatus.STOPPING); 
                return true; 
            case IntervalTimerStatus.TRIGGERED: 
                setTimerStatus(IntervalTimerStatus.TRIGGERED_HALT); 
                return true; 
            default: 
                return false; 
        } 
    }; 


    /* Handle starting and stopping the timer. TODO: Could this be removed? */
    React.useEffect(() => {
        if (timerStatus === IntervalTimerStatus.STARTING) 
        {
            if (timeInterval === 0) 
            {
                console.log("Interval Timer Started but no time set");
                setTimerStatus(IntervalTimerStatus.STOPPED);
            } 
            else 
            {
                setTimeRemaining(timeInterval);
                setTimerStatus(IntervalTimerStatus.RUNNING);
            }
        }
        if (timerStatus === IntervalTimerStatus.STOPPING) 
        {
            setTimerStatus(IntervalTimerStatus.STOPPED);
            setTimeRemaining(-1);
        }
    }, [timerStatus]);

    /* Updates time remaining and clock state every second. */
    React.useEffect(() => 
    {
        if (timerStatus !== IntervalTimerStatus.RUNNING) return;

        const intervalId = setInterval(() => 
        {
            if(timeRemaining <= 1)
            {
                setTimerStatus(IntervalTimerStatus.TRIGGERED);
                onTimerExpirationRef.current();

                if (loop) 
                {
                    setTimeRemaining(timeInterval);
                    setTimerStatus(IntervalTimerStatus.RUNNING);
                } 
                else 
                {
                    setTimerStatus(IntervalTimerStatus.STOPPED);
                    setTimeRemaining(-1);
                }
            }
            else
            {
                setTimeRemaining((prevTime) => prevTime -1);
            }

        }, 1000);

        return () => clearInterval(intervalId);

    }, [timerStatus, timeRemaining]);

    const formattedTimeRemaining = formatRemainingTime(timeRemaining); 

    return { 
    startTimer, 
    stopTimer, 
    timeRemaining, 
    formattedTimeRemaining, 
    timerStatus, 
    }; 
};

const formatRemainingTime = (timeInSeconds: number) => 
{ 
    if (timeInSeconds <= 0) 
    { 
        return "00:00"; 
    } 
    const minRemaining = String(Math.floor(timeInSeconds / 60)).padStart(2, '0'); 
    const secRemaining = String(Math.floor(timeInSeconds % 60)).padStart(2, '0'); 
    return `${minRemaining}:${secRemaining}`; 
}; 

export default useIntervalTimer;
